import type { Alpine as AlpineType } from 'alpinejs'

import {
  loadNotificationPreferences,
  NotificationSyncError,
  syncNotificationPreferences,
  type NotificationErrorReason,
  type NotificationTeamKey,
} from './onesignal'

export const PWA_ONBOARDING_KEY = 'lhc-pwa-onboarding-completed'
export const PWA_DEFAULT_TEAM_KEY = 'lhc-pwa-default-team'

/** Last preferences confirmed by OneSignal, shown while it loads. */
const PWA_NOTIFICATIONS_KEY = 'lhc-pwa-notifications'

export const emptyNotifications = (): Record<NotificationTeamKey, boolean> => ({
  lions: false,
  lionnes: false,
  u21: false,
  club: false,
})

export const getDefaultTeam = (): NotificationTeamKey | null => {
  const value = localStorage.getItem(PWA_DEFAULT_TEAM_KEY)
  if (value === 'lions' || value === 'lionnes' || value === 'u21' || value === 'club') {
    return value
  }
  return null
}

const getCachedNotifications = (): Record<NotificationTeamKey, boolean> | null => {
  try {
    const value = JSON.parse(localStorage.getItem(PWA_NOTIFICATIONS_KEY) || 'null')
    return value ? { ...emptyNotifications(), ...value } : null
  } catch {
    return null
  }
}

const cacheNotifications = (notifications: Record<NotificationTeamKey, boolean>): void => {
  try {
    localStorage.setItem(PWA_NOTIFICATIONS_KEY, JSON.stringify(notifications))
  } catch {
    // Only costs the instant display on next load
  }
}

export const applyDefaultTeamCalendarNav = (): void => {
  const links = document.querySelectorAll<HTMLAnchorElement>('[data-pwa-calendar-nav]')
  if (!links.length) return

  const team = getDefaultTeam()

  links.forEach((link) => {
    try {
      const urls = JSON.parse(link.dataset.calendarUrls || '{}') as Record<string, string>
      const fallback = link.dataset.calendarFallback || urls.lions || link.getAttribute('href') || '/'
      link.href = (team && urls[team]) || fallback
    } catch {
      // Keep Twig fallback href
    }
  })
}

export type NotificationStatus = 'loading' | 'idle' | 'saving' | 'saved' | 'error'

/** How long the "saved" confirmation stays visible. */
const SAVED_FEEDBACK_MS = 3000

export type PwaPrefsStore = {
  defaultTeam: NotificationTeamKey
  notifications: Record<NotificationTeamKey, boolean>
  status: NotificationStatus
  error: NotificationErrorReason | null
  isLocked(): boolean
  loadDefaultTeam(): void
  loadNotifications(): Promise<void>
  setDefaultTeam(team: NotificationTeamKey): void
  toggleNotificationDraft(key: NotificationTeamKey): void
  syncNotifications(notifications?: Record<NotificationTeamKey, boolean>): Promise<void>
  toggleNotification(key: NotificationTeamKey): Promise<void>
}

export const registerPwaPrefsStore = (Alpine: AlpineType): void => {
  Alpine.store('pwaPrefs', {
    defaultTeam: 'lions' as NotificationTeamKey,
    notifications: emptyNotifications(),
    status: 'loading' as NotificationStatus,
    error: null as NotificationErrorReason | null,

    /** Toggles wait for OneSignal, and only one write runs at a time. */
    isLocked(this: PwaPrefsStore): boolean {
      return this.status === 'loading' || this.status === 'saving' || this.error === 'unsupported'
    },

    loadDefaultTeam(this: PwaPrefsStore) {
      this.defaultTeam = getDefaultTeam() || 'lions'
    },

    async loadNotifications(this: PwaPrefsStore) {
      this.status = 'loading'
      this.error = null
      this.notifications = getCachedNotifications() || emptyNotifications()

      const { notifications, error } = await loadNotificationPreferences()
      if (error === 'unavailable') {
        // Last confirmed state beats the SDK's possibly stale local copy.
        this.notifications = getCachedNotifications() || notifications
      } else {
        this.notifications = notifications
        cacheNotifications(notifications)
      }
      this.error = error
      this.status = error ? 'error' : 'idle'
    },

    setDefaultTeam(this: PwaPrefsStore, team: NotificationTeamKey) {
      this.defaultTeam = team
      localStorage.setItem(PWA_DEFAULT_TEAM_KEY, team)
      applyDefaultTeamCalendarNav()
    },

    toggleNotificationDraft(this: PwaPrefsStore, key: NotificationTeamKey) {
      this.notifications = { ...this.notifications, [key]: !this.notifications[key] }
    },

    async syncNotifications(
      this: PwaPrefsStore,
      notifications = this.notifications,
    ): Promise<void> {
      await syncNotificationPreferences(notifications)
      this.notifications = { ...notifications }
      cacheNotifications(this.notifications)
    },

    async toggleNotification(this: PwaPrefsStore, key: NotificationTeamKey) {
      const previous = { ...this.notifications }
      const next = { ...this.notifications, [key]: !this.notifications[key] }
      this.notifications = next
      this.status = 'saving'
      this.error = null
      try {
        await syncNotificationPreferences(next)
        cacheNotifications(next)
        this.status = 'saved'
        setTimeout(() => {
          if (this.status === 'saved') this.status = 'idle'
        }, SAVED_FEEDBACK_MS)
      } catch (error) {
        console.error('OneSignal sync failed:', error)
        const reason = error instanceof NotificationSyncError ? error.reason : 'unavailable'

        // An unconfirmed write may still land: show what OneSignal holds now.
        const current = reason === 'unconfirmed' ? await loadNotificationPreferences() : null
        this.notifications = current && !current.error ? current.notifications : previous
        if (current && !current.error) cacheNotifications(current.notifications)

        this.error = reason
        this.status = 'error'
      }
    },
  })
}
